import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IconMenuTwo, IconUserFilled, IconXTwo } from '../../../assets/icons/index';
import { StoreState } from '../../../types/store.type';
import { UserState } from '../../../types/user.store.type';
import { RequiredActions } from 'guest-data-types-ts-lib';
import { Language, Currency } from '../../../types/preference.type';
import { AuthUtils, ValidationUtils, SharedUtils, ReservationUtils } from '../../../utils';
import { setLanguage, setCurrency, fetchTrip } from '../../../store/actions';
import horizontalLogo from '../../../assets/images/vacasa-logo-horizontal-blue.svg';
import languages from '../../../assets/languages.json';
import currencies from '../../../assets/currencies.json';
import { InitialLanguage } from '../../../types/preference.type';
import { LeftMobileNavMenu, LeftNavBarMenu, RightMobileNavMenu, RightNavBarMenu } from '../..';
import _ from 'lodash';
import $ from 'jquery';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { CookieKeys } from '../../../types/keys.type';
import ddLogger from '../../../logging/ddLogger';
import './NavBar.scss';

export const NavBar = () => {
    const { reservation, guests, required_actions, unit, stay } = useSelector((store: StoreState) => store.trip);
    const user: UserState = useSelector((store: StoreState) => store.user);
    const [rightTopNavbarIcon, setRightTopNavbarIcon] = useState('user');
    const [mobileCurrency, setMobileCurrency] = useState('');
    const [mobileLanguage, setMobileLanguage] = useState('');
    let guestName, confirmationCode;
    const dispatch = useDispatch();
    let localPhoneNumber: string = '';
    let tollFreePhoneNumber: string = '';
    const caretOffset = SharedUtils.isBrowserFirefox() ? 0 : 21;
    const logoURL = ValidationUtils.validateAndGetEnvVar('REACT_APP_LOGO_URL');

    useEffect(() => {
        const languageCode = SharedUtils.getUserLanguageCode(user);
        setMobileLanguage(languageCode);
        setMobileCurrency(user.currency);
    }, []);

    const handleLogoClick = () => {
        window.location.assign(logoURL);
    };

    const updateCurrentLanguage = (languageCode: string) => {
        const language = languages.find((lang) => lang.code === languageCode);
        const initialLanguage = language || InitialLanguage;

        SharedUtils.setCookie(CookieKeys.PREFERRED_LANGUAGE, initialLanguage.code, { domain: `${ValidationUtils.getCookieDomain()}` });
        setMobileLanguage(initialLanguage.code);
        dispatch(setLanguage(language as Language));
        dispatch(fetchTrip(false, true));
    };

    const updateCurrentCurrency = (currencyCode: string) => {
        const currency = currencies.find((curr) => curr === currencyCode);
        const currencyInitial = currency || Currency.USD;

        SharedUtils.setCookie(CookieKeys.PREFERRED_CURRENCY, currencyInitial, { domain: `${ValidationUtils.getCookieDomain()}` });
        setMobileCurrency(currencyCode);
        dispatch(setCurrency(currency as Currency));
        dispatch(fetchTrip(false, true));
    };

    const handleLanguageChange = (languageCode: string) => {
        updateCurrentLanguage(languageCode);
    };

    const handleMobileLanguageChange = (e: any) => {
        updateCurrentLanguage(e.target.value);
        updateLanguageInputWidth();
    };

    const handleMobileCurrencyChange = (e: any) => {
        updateCurrentCurrency(e.target.value);
        updateCurrencyInputWidth();
    };

    const handleCurrencyChange = (currency: string) => {
        updateCurrentCurrency(currency);
    };

    const handleLogin = () => {
        AuthUtils.goToMyTrips();
    };

    const handleLogout = () => {
        AuthUtils.goToLogout();
    };

    const handleLeftMenuClick = () => {
        const navbarUser = document.getElementById('navbar-user');
        const navbar = document.getElementById('navbar-main');
        const navPanel = document.getElementById('sidenavbar-left');
        const iconMenu = document.getElementById('left-icon-menu');
        const iconClose = document.getElementById('left-icon-close');
        const iconUserFilled = document.getElementById('right-icon-user');
        const notifications = document.getElementById('mobile-notifications');

        if (!navbarUser || !navbar || !navPanel || !iconMenu || !iconClose || !iconUserFilled) {
            return;
        }

        navbarUser.classList.toggle('fixed-top');
        navPanel.classList.toggle('left-inactive');
        iconMenu.classList.toggle('inactive');
        iconClose.classList.toggle('inactive');
        iconUserFilled.classList.toggle('hidden-icon');
        document.body.classList.toggle('no-scroll');
        navbar.classList.toggle('mb-5');
        if (notifications) {
            notifications.classList.toggle('hidden-icon');
        }

        if (navPanel.classList.contains('left-inactive')) {
            enableBodyScroll(navPanel);
        } else {
            disableBodyScroll(navPanel);
        }
    };

    const handleRightMenuClick = () => {
        const navbarUser = document.getElementById('navbar-user');
        const rightMenu = document.getElementById('sidenavbar-right');
        const leftIconMenu = document.getElementById('left-icon-menu');
        const notifications = document.getElementById('mobile-notifications');

        if (!navbarUser || !rightMenu || !leftIconMenu) {
            return;
        }

        navbarUser.classList.toggle('fixed-top');
        rightMenu.classList.toggle('right-inactive');
        document.body.classList.toggle('no-scroll');
        leftIconMenu.classList.toggle('hidden-icon');
        rightTopNavbarIcon === 'user' ? setRightTopNavbarIcon('close') : setRightTopNavbarIcon('user');
        if (notifications) {
            notifications.classList.toggle('hidden-icon');
        }

        if (rightMenu.classList.contains('right-inactive')) {
            enableBodyScroll(rightMenu);
        } else {
            disableBodyScroll(rightMenu);
        }
    };

    const getReservationNotifications = (requiredActions?: RequiredActions): number => {
        if (!requiredActions) {
            return 0;
        }

        return _.reduce(requiredActions, (sum, value) => (value !== undefined && !value.completed ? sum + 1 : sum), 0);
    };

    if (unit && guests && reservation) {
        localPhoneNumber = unit.local_phone_number;
        tollFreePhoneNumber = unit.toll_free_phone_number;
        guestName = `${guests.primary.first_name} ${guests.primary.last_name}`;
        confirmationCode = reservation.confirmation_code
            ? ReservationUtils.formatConfirmationCode(reservation.confirmation_code)
            : reservation.id;
    }
    const noLabelPhoneNumber = unit?.pms_data?.property_management?.phone ?? undefined;

    const updateLanguageInputWidth = () => {
        try {
            $('#language_hidden_option').html($('#language-link-mobile option:selected').text());
            const hiddenInputWidth = $('#language_hidden_select').width();
            if (hiddenInputWidth) {
                $('#language-link-mobile').width(hiddenInputWidth - caretOffset);
            }
        } catch (e) {
            ddLogger.error('Error updating input width language', { error: { kind: e.message } });
        }
    };

    const updateCurrencyInputWidth = () => {
        try {
            $('#currency_hidden_option').html($('#currency-link-mobile option:selected').text());
            const hiddenInputWidth = $('#currency_hidden_select').width();
            if (hiddenInputWidth) {
                $('#currency-link-mobile').width(hiddenInputWidth - caretOffset);
            }
        } catch (e) {
            ddLogger.error('Error updating input width currency', { error: { kind: e.message } });
        }
    };

    const reservationNotifications = getReservationNotifications(required_actions);
    return (
        <div id="navbar-user">
            <LeftMobileNavMenu
                extend_stay_eligible={stay?.extend_stay_eligible}
                add_pet_adjustment={stay?.pets_adjustment_available}
                unit={unit}
                localPhoneNumber={localPhoneNumber}
                tollFreePhoneNumber={tollFreePhoneNumber}
                noLabelPhoneNumber={noLabelPhoneNumber}
                reservationNotifications={reservationNotifications}
                reservationSourceId={reservation?.reservation_source_id}
                confirmationCode={confirmationCode}
                guestName={guestName}
                onLeftMenuClick={handleLeftMenuClick}
                isPalReservation={ReservationUtils.isPalManaged(reservation)}
            />
            <RightMobileNavMenu
                user={user}
                language={mobileLanguage}
                currency={mobileCurrency}
                onLogin={handleLogin}
                onLogout={handleLogout}
                onCurrencyChange={handleMobileCurrencyChange}
                onLanguageChange={handleMobileLanguageChange}
                updateLanguageInputWidth={updateLanguageInputWidth}
                updateCurrencyInputWidth={updateCurrencyInputWidth}
            />
            <nav
                id="navbar-main"
                className="navbar navbar-expand-lg navbar-light bg-white container fix-pad"
                style={{ fontFamily: 'sans-serif' }}
            >
                <div className="hamburger-menu-wrapper">
                    <span className="navbar-toggler sidenavbar-left-toggler border-0">
                        <IconMenuTwo id="left-icon-menu" className="feather icon-menu pointer" onClick={handleLeftMenuClick} />
                        <IconXTwo
                            id="left-icon-close"
                            className="feather inactive icon-menu light-stroke pointer"
                            onClick={handleLeftMenuClick}
                        />
                    </span>
                    {reservationNotifications > 0 && (
                        <span id="mobile-notifications" className="side-notification d-lg-none mobile-safari mobile-firefox">
                            {reservationNotifications}
                        </span>
                    )}
                </div>
                <a id="navbar-vacasa-logo" className="navbar-brand-v2" onClick={handleLogoClick}>
                    <span>
                        <img className="d-lg-none horizontal-logo-v2" src={horizontalLogo} alt="Vacasa" />
                        <img className="d-none d-lg-block horizontal-logo-v2" src={horizontalLogo} alt="Vacasa" />
                    </span>
                </a>
                <span className="navbar-toggler sidenavbar-right-toggler border-0">
                    {rightTopNavbarIcon === 'user' ? (
                        <IconUserFilled id="right-icon-user" className="icon-user-filled icon-user-nav" onClick={handleRightMenuClick} />
                    ) : (
                        <IconXTwo id="right-icon-close" className="feather light-stroke" onClick={handleRightMenuClick} />
                    )}
                </span>
                <div className="collapse navbar-collapse">
                    <LeftNavBarMenu />
                    <RightNavBarMenu
                        user={user}
                        tollFreePhoneNumber={tollFreePhoneNumber}
                        localPhoneNumber={localPhoneNumber}
                        noLabelPhoneNumber={noLabelPhoneNumber}
                        onCurrencyChange={handleCurrencyChange}
                        onLanguageChange={handleLanguageChange}
                        onLogin={handleLogin}
                        onLogout={handleLogout}
                        updateLanguageInputWidth={updateLanguageInputWidth}
                        updateCurrencyInputWidth={updateCurrencyInputWidth}
                    />
                </div>
            </nav>
        </div>
    );
};
